import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/ThemeContext";
import { seedAPI } from "@/lib/api";
import WhatsAppButton from "@/components/WhatsAppButton";
import InstallPWA from "@/components/InstallPWA";
import PolicySignatureAlert from "@/components/PolicySignatureAlert";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AgentRegisterPage from "@/pages/AgentRegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import StudentDashboard from "@/pages/StudentDashboard";
import AgentDashboard from "@/pages/AgentDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CoursesPage from "@/pages/CoursesPage";
import DestinationsPage from "@/pages/DestinationsPage";
import ProfileBuilderPage from "@/pages/ProfileBuilderPage";
import CheckEligibilityPage from "@/pages/CheckEligibilityPage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import FinancesPage from "@/pages/FinancesPage";
import HelpPage from "@/pages/HelpPage";
import UniversityDetailPage from "@/pages/UniversityDetailPage";
import BlogPage from "@/pages/BlogPage";
import ArticleDetailPage from "@/pages/ArticleDetailPage";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import TrainingHub from "@/pages/TrainingHub";
import ScholarshipSearchPage from "@/pages/ScholarshipSearchPage";
import FooterDemoPage from "@/pages/FooterDemoPage";
import ApplicationDetailPage from "@/pages/ApplicationDetailPage";
import AgentFullPage from "@/pages/AgentFullPage";
import VisaProbabilityPage from "@/pages/VisaProbabilityPage";
import MarketingUploadTicketPage from "@/pages/MarketingUploadTicketPage";
import PnLCalculatorPage from "@/pages/PnLCalculatorPage";
import LaunchPage from "@/pages/LaunchPage";
import InvestorsPage from "@/pages/InvestorsPage";

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 flex items-center justify-center transition-colors">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (roles && !roles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'student') return <Navigate to="/student" replace />;
    if (user.role === 'agent') return <Navigate to="/agent" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Dashboard Router - redirects based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (user?.role === 'student') return <Navigate to="/student" replace />;
  if (user?.role === 'agent') return <Navigate to="/agent" replace />;
  if (user?.role === 'admin' || user?.role === 'employee') return <Navigate to="/admin" replace />;
  
  return <Navigate to="/" replace />;
};

function AppContent() {
  const { isAuthenticated, user } = useAuth();
  
  // Check if user is an employee who needs to see policy alert
  const showPolicyAlert = isAuthenticated && (user?.role === 'admin' || user?.role === 'employee');
  
  // Seed database on first load
  useEffect(() => {
    const initDatabase = async () => {
      try {
        await seedAPI.seed();
        console.log("Database initialized");
      } catch (e) {
        // Already seeded or error - ignore
        console.log("Database seed check complete");
      }
    };
    initDatabase();
  }, []);

  return (
    <BrowserRouter>
      {/* Policy Signature Alert for Employees */}
      {showPolicyAlert && <PolicySignatureAlert />}
      
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={isAuthenticated ? <DashboardRouter /> : <LoginPage />} />
        <Route path="/register" element={isAuthenticated ? <DashboardRouter /> : <RegisterPage />} />
        <Route path="/agent-register" element={isAuthenticated ? <DashboardRouter /> : <AgentRegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/destinations" element={<DestinationsPage />} />
        <Route path="/university/:id" element={<UniversityDetailPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<ArticleDetailPage />} />
        <Route path="/check-eligibility" element={<CheckEligibilityPage />} />
        <Route path="/visa-probability" element={<VisaProbabilityPage />} />
        <Route path="/applications" element={<ApplicationsPage />} />
        <Route path="/finances" element={<FinancesPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/training" element={<TrainingHub />} />
        <Route path="/scholarships" element={<ScholarshipSearchPage />} />
        <Route path="/marketing/upload/:ticketId" element={<MarketingUploadTicketPage />} />
        <Route path="/footer-demo" element={<FooterDemoPage />} />
        <Route path="/launch" element={<LaunchPage />} />
        <Route path="/investors" element={<InvestorsPage />} />
        
        {/* Protected Routes - Student */}
        <Route path="/student" element={
          <ProtectedRoute roles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        <Route path="/profile-builder" element={
          <ProtectedRoute roles={['student']}>
            <ProfileBuilderPage />
          </ProtectedRoute>
        } />
        
        {/* Protected Routes - Agent */}
        <Route path="/agent" element={
          <ProtectedRoute roles={['agent']}>
            <AgentDashboard />
          </ProtectedRoute>
        } />
        
        {/* Protected Routes - Admin & Employee */}
        <Route path="/admin" element={
          <ProtectedRoute roles={['admin', 'employee']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute roles={['admin', 'employee']}>
            <AnalyticsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/pnl-calculator" element={
          <ProtectedRoute roles={['admin', 'employee']}>
            <PnLCalculatorPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/applications/:id" element={
          <ProtectedRoute roles={['admin', 'employee']}>
            <ApplicationDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/agents/:agentId" element={
          <ProtectedRoute roles={['admin', 'employee']}>
            <AgentFullPage />
          </ProtectedRoute>
        } />
        
        {/* Application Detail for Students and Agents */}
        <Route path="/application/:id" element={
          <ProtectedRoute roles={['student', 'agent', 'admin', 'employee']}>
            <ApplicationDetailPage />
          </ProtectedRoute>
        } />
        
        {/* Dashboard redirect */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster richColors position="top-right" />
      <WhatsAppButton />
      <InstallPWA />
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
